import * as stylex from "@stylexjs/stylex";
import { AnimatePresence, motion } from "framer-motion";
import { useMedia } from "hooks/useMedia";
import { Close16 } from "icons/Close";
import React, { useEffect, useMemo, useRef } from "react";
import { breakpoints, colors } from "../materials/consts.stylex";
const ClickAwayListener = ({ children, onClickAway })=>{
    const elementRef = useRef(null);
    const activatedRef = React.useRef(false);
    React.useEffect(()=>{
        // Ensure that this component is not "activated" synchronously.
        // https://github.com/facebook/react/issues/20074
        setTimeout(()=>{
            activatedRef.current = true;
        }, 0);
        return ()=>{
            activatedRef.current = false;
        };
    }, []);
    useEffect(()=>{
        const handleClick = (ev)=>{
            if (!elementRef.current || !activatedRef.current) {
                return;
            }
            const bcr = elementRef.current.getBoundingClientRect();
            const { clientX: cx, clientY: cy } = ev;
            if (cx < bcr.left || cx > bcr.right || cy < bcr.top || cy > bcr.bottom) {
                onClickAway?.();
            }
        };
        document.addEventListener("click", handleClick);
        return ()=>{
            document.removeEventListener("click", handleClick);
        };
    }, [
        onClickAway
    ]);
    return React.cloneElement(children, {
        ref: elementRef
    });
};
const getPopupStyle = (popupProps)=>{
    const { anchorOffset, anchorPosition, container, anchor } = popupProps;
    const style = {
        position: "absolute"
    };
    if (!container || !anchor) {
        return style;
    }
    const containerBcr = container.getBoundingClientRect();
    const anchorBcr = anchor.getBoundingClientRect();
    const top = anchorBcr.top - containerBcr.top + (anchorPosition.vertical === "end" ? anchorBcr.height : anchorPosition.vertical === "middle" ? anchorBcr.height / 2 : 0) + (anchorOffset?.vertical || 0);
    const left = anchorBcr.left - containerBcr.left + (anchorPosition.horizontal === "end" ? anchorBcr.width : anchorPosition.horizontal === "middle" ? anchorBcr.width / 2 : 0) + (anchorOffset?.horizontal || 0);
    style.top = top;
    style.left = left;
    return style;
};
const IconButton = ({ children, className, style, ...props })=>{
    const ownProps = {
        className: "x12klvby xx20hqb x1ypdohk x7gbtqy x9v0ylq"
    };
    return <div className={[
        ownProps.className,
        className
    ].filter(Boolean).join(" ")} style={{
        ...ownProps.style,
        ...style
    }} {...props}>
      {children}
    </div>;
};
const KeyListener = ({ onKey, name })=>{
    useEffect(()=>{
        const handle = (ev)=>{
            if (ev.key === name) {
                onKey?.();
            }
        };
        document.addEventListener("keydown", handle);
        return ()=>{
            document.removeEventListener("keydown", handle);
        };
    }, [
        name,
        onKey
    ]);
    return null;
};
const PopupDesktop = (popupProps)=>{
    const { children, style, onClose, open, showCloseButton = true } = popupProps;
    const computedPositionStyle = useMemo(()=>{
        return getPopupStyle(popupProps);
    }, [
        popupProps
    ]);
    return <AnimatePresence>
      {open ? <>
          <KeyListener name="Escape" onKey={onClose}/>
          <ClickAwayListener onClickAway={onClose}>
            <motion.div style={{
        ...style,
        ...computedPositionStyle
    }} initial={{
        opacity: 0,
        scaleX: 0,
        scaleY: 0,
        originX: 0,
        originY: 0
    }} animate={{
        opacity: 1,
        scaleX: 1,
        scaleY: 1
    }} exit={{
        opacity: 0,
        y: 100,
        transition: {
            opacity: {
                duration: 0.25
            },
            y: {
                duration: 0.3
            }
        }
    }}>
              {showCloseButton ? <IconButton className="x10l6tqk x1suesbl x1nxzj52 xhtitgo" onClick={onClose}>
                  <Close16/>
                </IconButton> : null}
              { /* Arrow shadow, arrow is transparent */ }
              <div className="x9tu13d x19co3pv xny8a46 x10l6tqk x1myihg1 x8l1umf x1jchvi3">▲</div>
              { /* Real arrow, no shadow */ }
              <div className="x9tu13d x1awj2ng x10l6tqk x1myihg1 x8l1umf x1vjfegm x1jchvi3">▲</div>
              <div className="xjp9rq6 x1uz70x1 xgv0goc x15kp5k8 x12oqio5">{children}</div>
            </motion.div>
          </ClickAwayListener>
        </> : false}
    </AnimatePresence>;
};
const PopupMobile = ({ open, children, onClose, showCloseButton = true })=>{
    return <AnimatePresence>
      {open ? <ClickAwayListener onClickAway={onClose}>
          <motion.div initial={{
        position: "fixed",
        y: 400,
        left: 0,
        right: 0,
        bottom: "env(safe-area-inset-bottom, 0)",
        borderRadius: 10,
        overflow: "hidden",
        maxHeight: "80vh",
        boxShadow: "0 -10px 10px rgba(0, 0, 0, 0.25)",
        background: "white",
        padding: "1rem"
    }} animate={{
        y: 0
    }} transition={{
        type: "spring",
        stiffness: 200,
        damping: 20
    }} exit={{
        y: 400
    }}>
            {showCloseButton ? <IconButton className="x10l6tqk x1suesbl x1nxzj52 xhtitgo" onClick={onClose}>
                <Close16/>
              </IconButton> : null}
            {children}
          </motion.div>
        </ClickAwayListener> : null}
    </AnimatePresence>;
};
export const Popup = (props)=>{
    const screenMDown = !useMedia(breakpoints.m);
    return screenMDown ? <PopupMobile {...props}/> : <PopupDesktop {...props}/>;
};
